import { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, ChevronLeft } from 'lucide-react';
import { apiGet } from '@/lib/api';
import { formatDateTime } from '@/lib/formatters';
import { groupThreadsByClient } from '@/lib/groupThreads';
import MessagesPanel from '@/components/shared/MessagesPanel';
import styles from './MessagesInbox.module.css';

/**
 * Every conversation in one place.
 *
 * Conversations otherwise live on the application detail view, so answering
 * three funders across two cases means three separate navigations. This is the
 * workspace: what needs a reply on the left, the conversation on the right.
 *
 * The left nav groups by client and then by case. A lawyer carries many
 * clients, and one client can file more than one application over time, so the
 * client alone does not separate their threads. The case sub-heading appears
 * only when a client actually has a second matter — see lib/groupThreads.
 *
 * A funder's list is flat by construction rather than by a role check: they
 * have exactly one thread per application, so every group holds one row and the
 * grouping collapses to a list on its own.
 *
 * Below the breakpoint the two panes stop coexisting: the list is the page, and
 * choosing a conversation replaces it, with a back control — the way a phone
 * messages app behaves.
 */

// The sidebar refreshes more slowly than the open conversation: it is context,
// and a list that reshuffles under the cursor is worse than one a few seconds
// stale.
const LIST_POLL_MS = 30000;

const MessagesInbox = ({ currentUser }) => {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);   // { applicationId, threadId }
  const [loading, setLoading] = useState(true);
  const autoSelected = useRef(false);

  const load = useCallback(async () => {
    try {
      const all = await apiGet('/messages/threads');
      setThreads(Array.isArray(all) ? all : []);
    } catch (e) {
      console.error('Failed to load conversations:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep the sidebar current too: a new conversation, a changed preview or a
  // cleared unread state should appear without a page refresh. Slower than the
  // open thread's own poll — the list is context, not the conversation.
  useEffect(() => {
    const tick = () => { if (!document.hidden) load(); };
    const timer = setInterval(tick, LIST_POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  const groups = groupThreadsByClient(threads);

  // Two rules, both variations on "show whatever the viewer is not".
  //
  // Group headers name the client, so they only earn their place where the
  // client varies: a lawyer, paralegal or admin sees many. A client would get
  // their own name as a header on every group; a funder would get one header
  // per single row.
  //
  // The row then names the other party — the client for a funder, the funder
  // for everyone else.
  const viewerIsFunder = currentUser?.role === 'funder';
  const showGroupHeaders = ['lawyer', 'paralegal', 'admin'].includes(currentUser?.role);

  // Open the most recently active conversation so the page is never a blank
  // right pane on arrival — but only once. Re-running whenever `selected` is
  // null defeated the mobile back control: clearing the selection immediately
  // reselected the same conversation.
  useEffect(() => {
    if (autoSelected.current || groups.length === 0) return;
    autoSelected.current = true;
    const first = groups[0].cases[0];
    setSelected({ applicationId: first.applicationId, threadId: first.threads[0].thread_id });
  }, [groups]);

  if (loading) {
    return <div className={styles.blank} data-testid="inbox-loading">Loading conversations…</div>;
  }

  if (threads.length === 0) {
    return (
      <div className={styles.blank} data-testid="inbox-empty">
        <MessageSquare size={24} aria-hidden="true" />
        <p className={styles.blankTitle}>No conversations yet</p>
        <p className={styles.blankText}>
          Conversations about an application appear here once someone starts one.
        </p>
      </div>
    );
  }

  const totalUnread = groups.reduce((n, g) => n + g.unreadCount, 0);

  return (
    <div
      className={styles.inbox}
      data-testid="messages-inbox"
      data-pane={selected ? 'thread' : 'list'}
    >
      <aside className={styles.sidebar} data-testid="inbox-sidebar">
        <div className={styles.sidebarHead}>
          <strong>Conversations</strong>
          {totalUnread > 0 && (
            <span className={styles.count} data-testid="inbox-unread-total">{totalUnread}</span>
          )}
        </div>

        <div className={styles.list}>
          {groups.map((group) => (
            <div key={group.key} className={styles.group} data-testid={`inbox-client-${group.key}`}>
              {showGroupHeaders && (
                <div className={styles.groupHead}>
                  <span className={styles.clientName}>{group.clientName}</span>
                  {group.unreadCount > 0 && (
                    <span className={styles.groupCount} data-testid="inbox-client-unread">
                      {group.unreadCount}
                    </span>
                  )}
                </div>
              )}

              {group.cases.map((c) => (
                <div key={c.applicationId} data-testid={`inbox-case-${c.applicationId}`}>
                  {/* Only when this client has more than one matter. */}
                  {showGroupHeaders && group.showCaseHeadings && c.label && (
                    <div className={styles.caseHead} data-testid="inbox-case-heading">{c.label}</div>
                  )}

                  {c.threads.map((t) => (
                    <button
                      key={t.thread_id}
                      className={`${styles.row} ${selected?.threadId === t.thread_id ? styles.rowActive : ''}`}
                      onClick={() => setSelected({ applicationId: c.applicationId, threadId: t.thread_id })}
                      data-testid={`inbox-thread-${t.thread_id}`}
                      data-unread={t.unread ? 'true' : 'false'}
                      aria-current={selected?.threadId === t.thread_id ? 'true' : undefined}
                    >
                      <span className={styles.rowTop}>
                        <span className={styles.rowName}>
                          {viewerIsFunder
                            ? (group.clientName || t.funder_company)
                            : (t.funder_company || 'Funder')}
                        </span>
                        {t.last_message_at && (
                          <span className={styles.rowTime}>{formatDateTime(t.last_message_at)}</span>
                        )}
                      </span>
                      <span className={styles.rowPreview}>
                        {t.last_message_preview || 'No messages yet'}
                      </span>
                      {/* Without a case heading above, the row still has to say
                          which matter it belongs to when there is more than one. */}
                      {!(showGroupHeaders && group.showCaseHeadings) && c.label && (
                        <span className={styles.rowCase}>{c.label}</span>
                      )}
                      {t.unread && <span className={styles.rowDot} aria-label="Unread" />}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </aside>

      <section className={styles.pane} data-testid="inbox-pane">
        <button
          className={styles.back}
          onClick={() => setSelected(null)}
          data-testid="inbox-back"
        >
          <ChevronLeft size={16} /> All conversations
        </button>

        {selected ? (
          <MessagesPanel
            key={selected.threadId}
            applicationId={selected.applicationId}
            currentUser={currentUser}
            initialThreadId={selected.threadId}
            fill
            onActivity={load}
          />
        ) : (
          <div className={styles.blank} data-testid="inbox-no-selection">
            <MessageSquare size={22} aria-hidden="true" />
            <p className={styles.blankText}>Choose a conversation.</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default MessagesInbox;
